import React from 'react';
import { X, Tv, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';

const Toast = ({ notification, onClose }) => {
  const navigate = useNavigate();

  const getIcon = () => {
    switch (notification.type) {
      case 'watch-party':
        return <Tv className="w-5 h-5 text-emerald-400" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-emerald-400" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      case 'info':
      default:
        return <Info className="w-5 h-5 text-blue-400" />;
    }
  };

  const handleClick = () => {
    if (notification.onClick) {
      notification.onClick();
    } else if (notification.link) {
      navigate(notification.link);
    }
    onClose();
  };

  return (
    <div
      className="group relative flex items-start gap-3 p-4 mb-3 
        bg-gradient-to-br from-slate-900/95 to-slate-950/95 
        border border-white/10 rounded-xl 
        shadow-[0_8px_32px_rgba(0,0,0,0.4)] 
        backdrop-blur-xl
        hover:border-emerald-400/30 
        transition-all duration-300
        animate-slide-in-right
        cursor-pointer"
      onClick={handleClick}
      role="button"
      tabIndex={0}
    >
      {/* Icon */}
      <div className="shrink-0 mt-0.5">
        {getIcon()}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {notification.title && (
          <p className="text-sm font-semibold text-white mb-0.5">
            {notification.title}
          </p>
        )}
        <p className="text-sm text-slate-300 leading-snug">
          {notification.message}
        </p>
        {notification.subtitle && (
          <p className="text-xs text-slate-400 mt-1">
            {notification.subtitle}
          </p>
        )}
      </div>

      {/* Close Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="shrink-0 p-1.5 rounded-lg
          text-slate-400 hover:text-white 
          hover:bg-white/10 
          transition-colors
          opacity-0 group-hover:opacity-100"
        aria-label="Close notification"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Progress bar (if duration is set) */}
      {notification.duration && notification.duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/5 rounded-b-xl overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 animate-progress"
            style={{
              animationDuration: `${notification.duration}ms`,
            }}
          />
        </div>
      )}
    </div>
  );
};

const ToastContainer = () => {
  const { notifications, removeNotification } = useNotifications();

  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] max-w-sm w-full pointer-events-none">
      <div className="pointer-events-auto">
        {notifications.map((notification) => (
          <Toast
            key={notification.id}
            notification={notification}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default ToastContainer;
